import React, { useState, useEffect } from 'react';
import usersAPI from '../../api/users';
import axios from 'axios';
import { getAuth } from 'firebase/auth';

export default function SignupStep2({ firebaseUid, email, onComplete }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [uploadedPublicId, setUploadedPublicId] = useState('');
  const [statesList, setStatesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [socialLinks, setSocialLinks] = useState({ instagram: '', facebook: '', twitter: '', tiktok: '', website: '' });

  useEffect(() => {
    const fetchStates = async () => {
      if (country === 'USA') {
        try {
          const res = await axios.get('/api/dropdowns/category/statesUSA');
          console.log('Fetched states:', res.data);
          setStatesList(res.data.options || []);
        } catch (err) {
          console.error('Failed to load state list:', err);
        }
      } else {
        setStatesList([]);
      }
    };
    fetchStates();
  }, [country]);

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();

      const res = await fetch('/api/uploads/profile', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!data.url) throw new Error('No URL returned');

      setProfileImage(data.url);
      setUploadedPublicId(data.public_id);
      setMessage('✅ Profile image uploaded!');
    } catch (err) {
      console.error('Upload error:', err);
      setMessage('❌ Failed to upload image.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!uploadedPublicId) return;
    try {
      await axios.post('/api/uploads/delete', {
        public_id: uploadedPublicId,
        resource_type: 'image',
      });
      setProfileImage('');
      setUploadedPublicId('');
      setMessage('🗑️ Image removed');
    } catch (err) {
      console.error(err);
      setMessage('❌ Failed to remove image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const coordsRes = await axios.get(`/api/utils/coords-from-zip/${zip}`);
      const coords = [coordsRes.data.lon, coordsRes.data.lat];

      const formattedAddress = `${city}, ${state}, ${zip}`;

      const updatePayload = {
        firstName,
        lastName,
        profileImage,
        socialLinks,
        address: {
          country,
          state,
          city,
          zip,
        },
        location: {
          type: 'Point',
          coordinates: coords,
          formattedAddress,
        },
      };

      await usersAPI.updateProfile(updatePayload);
      setMessage('✅ Profile updated!');
      setTimeout(() => {
        onComplete();
      }, 500);
    } catch (err) {
      setMessage('❌ Failed to update profile: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      <h2 className="text-xl font-semibold mb-2">Complete Your Profile</h2>

      <input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="border p-2" required />
      <input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} className="border p-2" required />

      <div>
        <label className="block mb-1 font-medium">Upload Profile Image</label>
        {profileImage ? (
          <div className="relative w-32 h-32">
            <img src={profileImage} alt="Uploaded" className="rounded-full object-cover w-32 h-32" />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-0 right-0 bg-red-600 text-white text-xs px-2 py-1 rounded"
            >
              ✕
            </button>
          </div>
        ) : (
          <input type="file" accept="image/*" onChange={handleProfileImageUpload} className="border p-2 w-full" />
        )}
      </div>

      <select value={country} onChange={(e) => setCountry(e.target.value)} className="border p-2" required>
        <option value="">Select Country</option>
        <option value="USA">United States</option>
        <option value="Canada">Canada</option>
        <option value="UK">United Kingdom</option>
        <option value="Other">Other</option>
      </select>

      {country === 'USA' ? (
        <select value={state} onChange={(e) => setState(e.target.value)} className="border p-2" required>
          <option value="">Select State</option>
          {statesList.map((s, i) => (
            <option key={i} value={s}>{s}</option>
          ))}
        </select>
      ) : (
        <input type="text" placeholder="State/Province" value={state} onChange={(e) => setState(e.target.value)} className="border p-2" required />
      )}

      <input type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} className="border p-2" required />
      <input type="text" placeholder="Zip Code" value={zip} onChange={(e) => setZip(e.target.value)} className="border p-2" required />

      <input type="text" placeholder="Instagram URL" value={socialLinks.instagram} onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })} className="border p-2" />
      <input type="text" placeholder="Facebook URL" value={socialLinks.facebook} onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })} className="border p-2" />
      <input type="text" placeholder="X (Twitter) URL" value={socialLinks.twitter} onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })} className="border p-2" />
      <input type="text" placeholder="TikTok URL" value={socialLinks.tiktok} onChange={(e) => setSocialLinks({ ...socialLinks, tiktok: e.target.value })} className="border p-2" />
      <input type="text" placeholder="Website URL" value={socialLinks.website} onChange={(e) => setSocialLinks({ ...socialLinks, website: e.target.value })} className="border p-2" />

      {message && <p className="text-sm text-center text-gray-600">{message}</p>}

      <button type="submit" disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded">
        {loading ? 'Saving...' : 'Save & Continue'}
      </button>
    </form>
  );
}
